import React, { useEffect, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { FiMessageSquare, FiPhoneCall, FiSend, FiUsers, FiX, FiDelete, FiPause, FiPlay, FiPhoneOff, FiClock, FiCalendar, FiRefreshCw, } from 'react-icons/fi';
import CallSummary from './CallSummary';
import DialerHistory, { type CallHistoryItem } from './DialerHistory';
import DialerSmsHistory, { type SmsHistoryItem } from './DialerSmsHistory';
import { useToast } from '../../context/ToastContext';
import { useDialerCallDetail } from '../../context/DialerCallDetailContext';
import '../../styles/Dialer.css';
import '../../styles/CarrierDownload.css';
type DatePreset = 'today' | 'week' | 'month';
function toYMD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
interface ContactRecord {
    name: string;
    phone: string;
    databaseId: string;
}
const KEYPAD_LETTERS: Record<string, string> = {
    '1': '', '2': 'ABC', '3': 'DEF',
    '4': 'GHI', '5': 'JKL', '6': 'MNO',
    '7': 'PQRS', '8': 'TUV', '9': 'WXYZ',
    '*': '', '0': '+', '#': '',
};
function formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
const DialerPage: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const { dialerCallDetail, setDialerCallDetail } = useDialerCallDetail();
    const [activeTab, setActiveTab] = useState<'dialer' | 'history'>('dialer');
    const [device, setDevice] = useState<Device | null>(null);
    const [deviceError, setDeviceError] = useState<string | null>(null);
    const [status, setStatus] = useState<'' | 'Calling...' | 'Connected' | 'Disconnected' | 'Failed to connect call'>('');
    const [number, setNumber] = useState('');
    const [duration, setDuration] = useState(0);
    const durationRef = useRef(0);
    const [finalDuration, setFinalDuration] = useState(0);
    const [showContactSearch, setShowContactSearch] = useState(false);
    const [selectedContactName, setSelectedContactName] = useState('');
    const [insuredId, setInsuredId] = useState('');
    const [searchClientList, setSearchClientList] = useState<ContactRecord[]>([]);
    const [searchClientLoading, setSearchClientLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showMessageDialog, setShowMessageDialog] = useState(false);
    const [messageText, setMessageText] = useState('');
    const [smsSending, setSmsSending] = useState(false);
    const [parentCallSid, setParentCallSid] = useState('');
    const [onHold, setOnHold] = useState(false);
    const [activeCall, setActiveCall] = useState<any>(null);
    const [historyDataType, setHistoryDataType] = useState<'call' | 'sms'>('call');
    const [historyItems, setHistoryItems] = useState<CallHistoryItem[]>([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [historyError, setHistoryError] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyItemsPerPage, setHistoryItemsPerPage] = useState(20);
    const [smsHistoryItems, setSmsHistoryItems] = useState<SmsHistoryItem[]>([]);
    const [smsHistoryTotal, setSmsHistoryTotal] = useState(0);
    const [smsHistoryLoading, setSmsHistoryLoading] = useState(true);
    const [smsHistoryError, setSmsHistoryError] = useState(false);
    const [smsHistoryPage, setSmsHistoryPage] = useState(1);
    const [smsHistoryItemsPerPage, setSmsHistoryItemsPerPage] = useState(20);
    const [historyFromDate, setHistoryFromDate] = useState(() => {
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - 6);
        return toYMD(weekStart);
    });
    const [historyToDate, setHistoryToDate] = useState(() => toYMD(new Date()));
    const [dateFilterOpen, setDateFilterOpen] = useState(false);
    const dateFilterMenuRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const initTwilio = async () => {
            try {
                const authToken = localStorage.getItem('authToken') || '';
                const res = await fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/Twilio/token`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                });
                const data = await res.json().catch(() => null);
                if (!res.ok || data?.success === false) {
                    const msg = data?.errorMessage || 'Twilio is not configured for your agency. Please add your Twilio credentials in Dialer settings.';
                    setDeviceError(msg);
                    showError(msg);
                    return;
                }
                const twilioDevice = new Device(data.token);
                twilioDevice.on('disconnect', () => setStatus('Disconnected'));
                setDevice(twilioDevice);
            }
            catch {
                setDeviceError('Unable to initialize the dialer. Please try again later.');
            }
        };
        initTwilio();
    }, []);
    useEffect(() => {
        let timer: ReturnType<typeof setInterval> | undefined;
        if (status === 'Connected') {
            timer = setInterval(() => {
                durationRef.current += 1;
                setDuration(durationRef.current);
            }, 1000);
        }
        else {
            durationRef.current = 0;
            setDuration(0);
        }
        return () => { if (timer)
            clearInterval(timer); };
    }, [status]);
    useEffect(() => {
        if (!showContactSearch)
            return;
        const delay = setTimeout(() => {
            getNowCertsClients(searchTerm.trim() === '' ? 'a' : searchTerm);
        }, 500);
        return () => clearTimeout(delay);
    }, [searchTerm, showContactSearch]);
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.isContentEditable)
                return;
            const key = e.key;
            if (/^[0-9*#+]$/.test(key))
                setNumber((prev) => prev + key);
            else if (key === 'Backspace')
                setNumber((prev) => prev.slice(0, -1));
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, []);
    useEffect(() => {
        if (number === '') {
            setSelectedContactName('');
            setInsuredId('');
        }
    }, [number]);
    const fetchHistory = (pageNumber: number, perPage: number, from: string, to: string) => {
        const authToken = localStorage.getItem('authToken') || '';
        setHistoryLoading(true);
        setHistoryError(false);
        const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: String(perPage) });
        if (from)
            params.set('fromDate', from);
        if (to)
            params.set('toDate', to);
        fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/twilio/history?${params.toString()}`, {
            headers: { Authorization: `Bearer ${authToken}` },
        })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((data) => {
            setHistoryItems(Array.isArray(data?.data) ? data.data : []);
            setHistoryTotal(data?.totalCount ?? 0);
        })
            .catch(() => {
            setHistoryItems([]);
            setHistoryTotal(0);
            setHistoryError(true);
        })
            .finally(() => setHistoryLoading(false));
    };
    const fetchSmsHistory = (pageNumber: number, perPage: number, from: string, to: string) => {
        const authToken = localStorage.getItem('authToken') || '';
        setSmsHistoryLoading(true);
        setSmsHistoryError(false);
        const params = new URLSearchParams({ pageNumber: String(pageNumber), pageSize: String(perPage) });
        if (from)
            params.set('fromDate', from);
        if (to)
            params.set('toDate', to);
        fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/twilio/smshistory?${params.toString()}`, {
            headers: { Authorization: `Bearer ${authToken}` },
        })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((data) => {
            setSmsHistoryItems(Array.isArray(data?.data) ? data.data : []);
            setSmsHistoryTotal(data?.totalCount ?? 0);
        })
            .catch(() => {
            setSmsHistoryItems([]);
            setSmsHistoryTotal(0);
            setSmsHistoryError(true);
        })
            .finally(() => setSmsHistoryLoading(false));
    };
    useEffect(() => {
        if (activeTab !== 'history' || historyDataType !== 'call')
            return;
        fetchHistory(historyPage, historyItemsPerPage, historyFromDate, historyToDate);
    }, [activeTab, historyDataType, historyPage, historyItemsPerPage, historyFromDate, historyToDate]);
    useEffect(() => {
        if (activeTab !== 'history' || historyDataType !== 'sms')
            return;
        fetchSmsHistory(smsHistoryPage, smsHistoryItemsPerPage, historyFromDate, historyToDate);
    }, [activeTab, historyDataType, smsHistoryPage, smsHistoryItemsPerPage, historyFromDate, historyToDate]);
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dateFilterMenuRef.current && !dateFilterMenuRef.current.contains(e.target as Node)) {
                setDateFilterOpen(false);
            }
        };
        if (dateFilterOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [dateFilterOpen]);
    const getActiveDatePreset = (): DatePreset | null => {
        if (!historyFromDate || !historyToDate)
            return null;
        const now = new Date();
        const today = toYMD(now);
        if (historyToDate !== today)
            return null;
        if (historyFromDate === today)
            return 'today';
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - 6);
        if (historyFromDate === toYMD(weekStart))
            return 'week';
        const monthStart = new Date(now);
        monthStart.setDate(monthStart.getDate() - 29);
        if (historyFromDate === toYMD(monthStart))
            return 'month';
        return null;
    };
    const applyHistoryDatePreset = (preset: DatePreset) => {
        const now = new Date();
        const to = toYMD(now);
        const start = new Date(now);
        if (preset === 'week')
            start.setDate(start.getDate() - 6);
        else if (preset === 'month')
            start.setDate(start.getDate() - 29);
        setHistoryFromDate(toYMD(start));
        setHistoryToDate(to);
        setHistoryPage(1);
        setSmsHistoryPage(1);
        setDateFilterOpen(false);
    };
    const handleHistoryItemsPerPageChange = (val: number) => {
        setHistoryItemsPerPage(val);
        setHistoryPage(1);
    };
    const handleSmsHistoryItemsPerPageChange = (val: number) => {
        setSmsHistoryItemsPerPage(val);
        setSmsHistoryPage(1);
    };
    const activeDatePreset = getActiveDatePreset();
    const historyDateFilterLabel = activeDatePreset === 'today' ? 'Today' : activeDatePreset === 'week' ? 'Last Week' : activeDatePreset === 'month' ? 'Last Month' : 'Date';
    const filteredContacts = searchClientList.filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const getNowCertsClients = (query: string) => {
        setSearchClientLoading(true);
        const nowCertsToken = localStorage.getItem('amsToken') || '';
        fetch(`${(process.env.REACT_APP_NOWCERTSAPI || 'https://nowcertsapi-stage.insurancegig.com')}/api/nowcert/customerlist?searchText=${encodeURIComponent(query)}&nowCertsToken=${encodeURIComponent(nowCertsToken)}`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('toolboxToken') || ''}`,
                'Content-Type': 'application/json',
            },
        })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((data) => setSearchClientList(Array.isArray(data) ? data : []))
            .catch(() => setSearchClientList([]))
            .finally(() => setSearchClientLoading(false));
    };
    const handleButtonClick = (value: string) => setNumber((prev) => prev + value);
    const handleClear = () => setNumber((prev) => prev.slice(0, -1));
    const onCallClick = async () => {
        if (!device || !number)
            return;
        setStatus('Calling...');
        try {
            const call = await device.connect({ params: { To: number, UserId: localStorage.getItem('userId') || '', AgencyId: localStorage.getItem('NCAgencyId') || '' } });
            call.on('accept', () => { setStatus('Connected'); setActiveCall(call); setParentCallSid(call.parameters.CallSid); });
            call.on('disconnect', () => {
                setFinalDuration(durationRef.current);
                setStatus('Disconnected');
                setActiveCall(null);
                setParentCallSid(call.parameters.CallSid);
            });
        }
        catch {
            setStatus('Failed to connect call');
        }
    };
    const onSelectContactForCall = (contact: ContactRecord) => {
        setSelectedContactName(contact.name);
        setInsuredId(contact.databaseId);
        setNumber((prev) => prev + contact.phone);
        setShowContactSearch(false);
    };
    const onInsuredSelectedInSummary = (insured: ContactRecord) => {
        setSelectedContactName(insured.name);
        setInsuredId(insured.databaseId);
    };
    const onCallEndClick = () => {
        if (device)
            device.disconnectAll();
    };
    const onHoldUnholdClicked = () => {
        if (!activeCall)
            return;
        activeCall.mute(!onHold);
        setOnHold(!onHold);
    };
    const onMessageClick = () => setShowMessageDialog((v) => !v);
    const onSendSMSClicked = () => {
        setSmsSending(true);
        const authToken = localStorage.getItem('authToken') || '';
        fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/Twilio/sendsms`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ Body: messageText, To: '+' + number }),
        })
            .then(async (r) => {
            const data = await r.json().catch(() => null);
            if (!r.ok || data?.success === false)
                throw new Error(data?.errorMessage || 'Failed to send SMS. Please try again.');
            return data;
        })
            .then(() => {
            showSuccess(`SMS sent to +${number}`);
            setShowMessageDialog(false);
            setMessageText('');
        })
            .catch((err) => showError(err?.message || 'Failed to send SMS. Please try again.'))
            .finally(() => setSmsSending(false));
    };
    const onContactClicked = () => {
        setShowContactSearch(true);
        setSearchTerm('');
        setSearchClientLoading(true);
    };
    const onCloseContactSearch = () => {
        setShowContactSearch(false);
        setSearchTerm('');
        setSearchClientList([]);
    };
    const onSkipButtonClick = () => {
        setStatus('');
        setNumber('');
        setSelectedContactName('');
        setShowContactSearch(false);
        setInsuredId('');
        setParentCallSid('');
    };
    const onSelectedContactNameClicked = () => {
        const agencyDomain = localStorage.getItem('agencyDomain');
        if (!agencyDomain || !insuredId)
            return;
        window.open(`${agencyDomain}/AMSINS/Insureds/Details/${insuredId}/Information`, '_blank');
    };
    return (<div className="dialer-page">
      <div className="dialer-shell">
        {status === '' && (<div className="dialer-tabs-row">
            <div className="dialer-tabs">
              <button className={`dialer-tab${activeTab === 'dialer' ? ' dialer-tab--active' : ''}`} onClick={() => { setActiveTab('dialer'); setDialerCallDetail(null); }}>
                <FiPhoneCall size={12}/>
                Dialer
              </button>
              <button className={`dialer-tab${activeTab === 'history' ? ' dialer-tab--active' : ''}`} onClick={() => setActiveTab('history')}>
                <FiClock size={12}/>
                History
              </button>
            </div>

            {activeTab === 'history' && (<div className="dialer-history-type-toggle">
                <button type="button" className={`dialer-icon-btn${historyDataType === 'call' ? ' dialer-icon-btn--active' : ''}`} onClick={() => setHistoryDataType('call')} title="Call history">
                  <FiPhoneCall size={14}/>
                </button>
                <button type="button" className={`dialer-icon-btn${historyDataType === 'sms' ? ' dialer-icon-btn--active' : ''}`} onClick={() => { setHistoryDataType('sms'); setDialerCallDetail(null); }} title="SMS history">
                  <FiMessageSquare size={14}/>
                </button>
              </div>)}

            {activeTab === 'history' && (<div className="dialer-history-controls">
                <div ref={dateFilterMenuRef} style={{ position: 'relative' }}>
                  <button type="button" className={`cdsl-date-filter-btn${activeDatePreset || historyFromDate || historyToDate ? ' cdsl-date-filter-btn--active' : ''}`} title="Filter by date" aria-haspopup="true" aria-expanded={dateFilterOpen} onClick={() => setDateFilterOpen((p) => !p)}>
                    <FiCalendar size={14}/>
                    <span className="cdsl-date-filter-label">{historyDateFilterLabel}</span>
                  </button>

                  {dateFilterOpen && (<div className="cdsl-date-filter-popover" role="menu" aria-label="Filter by date">
                      <div className="cdsl-filter-header">
                        <span className="cdsl-filter-label">Filter by date</span>
                        {(historyFromDate || historyToDate) && (<button type="button" className="cdsl-filter-clear" onClick={() => {
                            setHistoryFromDate('');
                            setHistoryToDate('');
                            setHistoryPage(1);
                            setSmsHistoryPage(1);
                        }}>
                            Clear
                          </button>)}
                      </div>

                      <div className="cdsl-preset-pills">
                        {([
                        { key: 'today', label: 'Today' },
                        { key: 'week', label: 'Last Week' },
                        { key: 'month', label: 'Last Month' },
                    ] as {
                        key: DatePreset;
                        label: string;
                    }[]).map((p) => (<button key={p.key} type="button" className={`cdsl-preset-pill${activeDatePreset === p.key ? ' cdsl-preset-pill--active' : ''}`} onClick={() => applyHistoryDatePreset(p.key)}>
                            {p.label}
                          </button>))}
                      </div>

                      <div className="cdsl-custom-dates">
                        <div className="cdsl-date-row">
                          <label className="cdsl-date-row-label">From</label>
                          <input type="date" className="cdsl-date-row-input" value={historyFromDate} onChange={(e) => { setHistoryFromDate(e.target.value); setHistoryPage(1); setSmsHistoryPage(1); }}/>
                        </div>
                        <div className="cdsl-date-row">
                          <label className="cdsl-date-row-label">To</label>
                          <input type="date" className="cdsl-date-row-input" value={historyToDate} onChange={(e) => { setHistoryToDate(e.target.value); setHistoryPage(1); setSmsHistoryPage(1); }}/>
                        </div>
                      </div>
                    </div>)}
                </div>

                <button className="cdsl-refresh-btn" onClick={() => historyDataType === 'call'
                    ? fetchHistory(historyPage, historyItemsPerPage, historyFromDate, historyToDate)
                    : fetchSmsHistory(smsHistoryPage, smsHistoryItemsPerPage, historyFromDate, historyToDate)} title="Refresh">
                  <FiRefreshCw size={14}/>
                </button>
              </div>)}
          </div>)}

        {status === '' && activeTab === 'history' ? (historyDataType === 'call' ? (<DialerHistory items={historyItems} loading={historyLoading} error={historyError} page={historyPage} onPageChange={setHistoryPage} itemsPerPage={historyItemsPerPage} onItemsPerPageChange={handleHistoryItemsPerPageChange} total={historyTotal} onSelectItem={(item) => setDialerCallDetail((prev) => (prev?.id === item.id ? null : item))} selectedId={dialerCallDetail?.id ?? null}/>) : (<DialerSmsHistory items={smsHistoryItems} loading={smsHistoryLoading} error={smsHistoryError} page={smsHistoryPage} onPageChange={setSmsHistoryPage} itemsPerPage={smsHistoryItemsPerPage} onItemsPerPageChange={handleSmsHistoryItemsPerPageChange} total={smsHistoryTotal}/>)) : (<div className="dialer-card-area">
        <div className="dialer-card">
        {status === 'Disconnected' ? (<CallSummary parentCallSid={parentCallSid} insuredId={insuredId} callDuration={formatDuration(finalDuration)} phoneNumber={number} contactName={selectedContactName} onSkipButtonClick={onSkipButtonClick} onInsuredSelected={onInsuredSelectedInSummary}/>) : status === '' ? (<div className="dialer-main-content">
          {showContactSearch ? (<div className="dialer-contact-search">
              <div className="dialer-contact-search__input-row">
                <input type="text" className="dialer-input" placeholder="Search contacts…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus/>
                <button className="dialer-icon-btn" onClick={onCloseContactSearch} title="Close">
                  <FiX size={14}/>
                </button>
              </div>
              <div className="dialer-contact-list">
                {searchClientLoading ? (<div className="dialer-empty">Loading contacts…</div>) : filteredContacts.length > 0 ? (filteredContacts.map((contact, i) => (<div key={i} className="dialer-contact-item">
                      <div className="dialer-contact-item__avatar"><FiUsers size={14}/></div>
                      <div className="dialer-contact-item__meta">
                        <span className="dialer-contact-item__name">{contact.name}</span>
                        <span className="dialer-contact-item__phone">{contact.phone}</span>
                      </div>
                      <div className="dialer-contact-item__actions">
                        <button className="dialer-icon-btn" onClick={() => onSelectContactForCall(contact)} title="Call">
                          <FiPhoneCall size={14}/>
                        </button>
                        <button className="dialer-icon-btn" onClick={() => { setSelectedContactName(contact.name); setShowContactSearch(false); setShowMessageDialog(true); }} title="Message">
                          <FiMessageSquare size={14}/>
                        </button>
                      </div>
                    </div>))) : (<div className="dialer-empty">No contacts found</div>)}
              </div>
            </div>) : (<>
              <div className="dialer-input-row">
                <PhoneInput country={'us'} value={number} onChange={(phone: string) => setNumber(phone)}/>
                <div className="dialer-input-icons">
                  <button className="dialer-icon-btn" onClick={onContactClicked} title="Contacts">
                    <FiUsers size={16}/>
                  </button>
                  {number && (<button className="dialer-icon-btn" onClick={handleClear} title="Backspace">
                      <FiDelete size={16}/>
                    </button>)}
                </div>
              </div>

              {selectedContactName && <div className="dialer-selected-contact">{selectedContactName}</div>}

              <div className="dialer-keypad-section">
                {showMessageDialog ? (<textarea className="dialer-textarea" placeholder="Type your message here…" rows={10} value={messageText} onChange={(e) => setMessageText(e.target.value)}/>) : (<div className="dialer-keypad-grid">
                    {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((key) => (<button key={key} className="dialer-key" onClick={() => handleButtonClick(key)}>
                        <span className="dialer-key__digit">{key}</span>
                        {KEYPAD_LETTERS[key] && <span className="dialer-key__letters">{KEYPAD_LETTERS[key]}</span>}
                      </button>))}
                  </div>)}
              </div>

              {showMessageDialog ? (<div className="dialer-action-row">
                  <button className="dialer-call-btn" onClick={onSendSMSClicked} disabled={smsSending || !messageText.trim()}>
                    <FiSend size={14}/> {smsSending ? 'Sending…' : 'Send'}
                  </button>
                  <button className="dialer-call-btn dialer-call-btn--secondary" onClick={onMessageClick}>Back</button>
                </div>) : (<div className="dialer-action-row">
                  <button className="dialer-call-btn" onClick={onCallClick} disabled={!device || !number} title={!device ? deviceError || undefined : undefined}>
                    <FiPhoneCall size={14}/> Call
                  </button>
                  <button className="dialer-call-btn dialer-call-btn--secondary" onClick={onMessageClick} disabled={!number}>
                    <FiMessageSquare size={14}/> SMS
                  </button>
                </div>)}
            </>)}
          </div>) : (<div className="dialer-call-status">
            {status === 'Calling...' && (<>
                <div className="dialer-status-icon dialer-status-icon--pulse"><FiPhoneCall size={22}/></div>
                <h2 className="dialer-call-status__name" onClick={onSelectedContactNameClicked}>
                  {selectedContactName || number}
                </h2>
                <p className="dialer-call-status__sub">Calling…</p>
                <p className="dialer-call-status__number">+{number}</p>
              </>)}
            {status === 'Connected' && (<>
                <div className="dialer-status-icon dialer-status-icon--active"><FiPhoneCall size={22}/></div>
                <h2 className="dialer-call-status__name" onClick={onSelectedContactNameClicked}>
                  {selectedContactName || number}
                </h2>
                <p className="dialer-call-status__sub">Connected</p>
                <p className="dialer-call-status__timer">{formatDuration(duration)}</p>
                <button className="dialer-icon-btn dialer-icon-btn--lg" onClick={onHoldUnholdClicked} title={onHold ? 'Resume' : 'Hold'}>
                  {onHold ? <FiPlay size={18}/> : <FiPause size={18}/>}
                </button>
              </>)}
            {status === 'Failed to connect call' && (<>
                <div className="dialer-status-icon dialer-status-icon--ended"><FiPhoneCall size={22}/></div>
                <p className="dialer-call-status__sub">Failed to connect call</p>
              </>)}
            {status === 'Connected' && (<button className="dialer-end-call-btn" onClick={onCallEndClick}>
                <FiPhoneOff size={14}/> End
              </button>)}
            {status === 'Failed to connect call' && (<button className="dialer-call-btn dialer-call-btn--secondary" onClick={onSkipButtonClick}>Back to Dialer</button>)}
          </div>)}
        </div>
        </div>)}
      </div>
    </div>);
};
export default DialerPage;
